import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  MdAdd,
  MdDelete,
  MdDescription,
  MdEdit,
  MdImage,
  MdInsertDriveFile,
} from 'react-icons/md';
import {
  AppInput,
  AppSelect,
  OutlineButton,
  PrimaryButton,
  SortableTh,
  StatusBadge,
} from '../../../../components/ui';
import { useTableSort } from '../../../../hooks/useTableSort';
import {
  deleteDocument,
  listDocuments,
  uploadDocument,
} from '../../../../repositories/document.repository';

const DOCUMENT_GROUPS = [
  { value: 'compliance', label: 'Compliance (PUC, Insurance, Fitness)' },
  { value: 'sides', label: 'Side photo (Front / Rear / Left / Right)' },
  { value: 'other', label: 'Other attachment' },
];

const SIDE_LABELS = ['Front', 'Rear', 'Left', 'Right'];

function inferKind(fileName) {
  const lower = String(fileName || '').toLowerCase();
  if (/\.(jpg|jpeg|png|webp|gif)$/.test(lower)) return 'image';
  if (/\.pdf$/.test(lower)) return 'pdf';
  return 'file';
}

function docIcon(kind) {
  if (kind === 'image') return MdImage;
  if (kind === 'pdf') return MdDescription;
  return MdInsertDriveFile;
}

const emptyForm = {
  label: '',
  group: 'compliance',
  fileName: '',
  kind: 'pdf',
  url: null,
  file: null,
};

function DocumentForm({ title, form, setForm, errors, fileInputRef }) {
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (form.url?.startsWith('blob:')) {
      URL.revokeObjectURL(form.url);
    }
    const kind = inferKind(file.name);
    setForm((f) => ({
      ...f,
      fileName: file.name,
      kind,
      url: kind === 'image' ? URL.createObjectURL(file) : null,
      file,
    }));
    e.target.value = '';
  };

  return (
    <Box
      p={4}
      bg="bg.input"
      borderRadius="sm"
      borderWidth="1px"
      borderColor="border.subtle"
    >
      <Heading size="xs" mb={3}>
        {title}
      </Heading>
      <VStack align="stretch" spacing={3}>
        <AppSelect
          label="Document group"
          isRequired
          value={form.group}
          onChange={(v) => setForm((f) => ({ ...f, group: v || 'compliance' }))}
          options={DOCUMENT_GROUPS}
        />
        <AppInput
          label="Label"
          isRequired
          value={form.label}
          onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
          placeholder="e.g. PUC certificate"
          isInvalid={!!errors.label}
          errorMessage={errors.label}
        />
        <Box>
          <Text fontSize="sm" fontWeight="600" mb={2}>
            File
          </Text>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf"
            hidden
            onChange={handleFile}
          />
          {form.fileName ? (
            <Flex
              align="center"
              justify="space-between"
              gap={2}
              p={3}
              bg="bg.surface"
              borderRadius="sm"
              borderWidth="1px"
              borderColor="border.subtle"
            >
              <Flex align="center" gap={2} minW={0}>
                <Icon as={docIcon(form.kind)} boxSize={5} color="text.secondary" />
                <Text fontSize="13px" fontFamily="mono" noOfLines={1}>
                  {form.fileName}
                </Text>
              </Flex>
              <OutlineButton size="xs" type="button" onClick={() => fileInputRef.current?.click()}>
                Replace
              </OutlineButton>
            </Flex>
          ) : (
            <OutlineButton
              type="button"
              w="100%"
              leftIcon={<MdAdd />}
              onClick={() => fileInputRef.current?.click()}
            >
              Choose file (image or PDF)
            </OutlineButton>
          )}
          {errors.fileName ? (
            <Text fontSize="12px" color="status.error" mt={1}>
              {errors.fileName}
            </Text>
          ) : null}
        </Box>
      </VStack>
    </Box>
  );
}

/**
 * Add, edit, or remove equipment documents (side photos + compliance + other).
 */
export function EquipmentDocumentsModal({
  isOpen,
  onClose,
  asset,
  initialGroup = null,
  onSaved,
}) {
  const toast = useToast();
  const addFileRef = useRef(null);
  const editFileRef = useRef(null);
  const [mode, setMode] = useState('list');
  const [groupFilter, setGroupFilter] = useState('');
  const [addForm, setAddForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState(emptyForm);
  const [editingDoc, setEditingDoc] = useState(null);
  const [errors, setErrors] = useState({});
  const [documents, setDocuments] = useState([]);
  const [busy, setBusy] = useState(false);

  const mapDocs = (rows) =>
    (Array.isArray(rows) ? rows : []).map((d) => ({
      id: d.id,
      group:
        /front|rear|left|right|side/i.test(d.docType || '')
          ? 'sides'
          : /puc|insurance|fitness/i.test(d.docType || '')
            ? 'compliance'
            : 'other',
      label: d.docType || 'Document',
      fileName: d.fileName || '',
      kind: (d.mimeType || '').includes('pdf') ? 'pdf' : 'image',
      url: d.url || null,
    }));

  const reloadDocs = async () => {
    if (!asset?.id) {
      setDocuments(asset?.documents || []);
      return;
    }
    const rows = await listDocuments({ ownerType: 'asset', ownerId: asset.id });
    setDocuments(mapDocs(rows));
  };

  useEffect(() => {
    if (!isOpen) return;
    setMode('list');
    setGroupFilter(initialGroup || '');
    setAddForm({ ...emptyForm, group: initialGroup || 'compliance' });
    setEditForm(emptyForm);
    setEditingDoc(null);
    setErrors({});
    reloadDocs().catch((err) => {
      toast({
        title: err?.message || 'Failed to load documents',
        status: 'error',
        position: 'top-right',
      });
      setDocuments(asset?.documents || []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialGroup, asset?.id, asset?.assetId]);

  const filteredDocs = useMemo(() => {
    if (!groupFilter) return documents;
    return documents.filter((d) => d.group === groupFilter);
  }, [documents, groupFilter]);

  const {
    sorted: sortedDocs,
    sort,
    toggleSort,
  } = useTableSort(filteredDocs);

  const validateForm = (form, requireFile) => {
    const next = {};
    if (!form.label.trim()) next.label = 'Label is required';
    if (requireFile && !form.fileName.trim()) next.fileName = 'Choose a file';
    return next;
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const next = validateForm(addForm, true);
    setErrors(next);
    if (Object.keys(next).length) return;
    if (!asset?.id || !addForm.file) {
      toast({
        title: !asset?.id ? 'Missing equipment id' : 'Choose a file',
        status: 'error',
        position: 'top-right',
      });
      return;
    }
    setBusy(true);
    try {
      await uploadDocument({
        ownerType: 'asset',
        ownerId: asset.id,
        docType: addForm.label.trim() || addForm.group,
        file: addForm.file,
      });
      toast({
        title: 'Document uploaded',
        description: addForm.label.trim(),
        status: 'success',
        position: 'top-right',
      });
      setAddForm({ ...emptyForm, group: initialGroup || 'compliance' });
      setMode('list');
      await reloadDocs();
      onSaved?.();
    } catch (err) {
      toast({
        title: err?.message || 'Upload failed',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (doc) => {
    setEditingDoc(doc);
    setEditForm({
      label: doc.label || '',
      group: doc.group || 'compliance',
      fileName: doc.fileName || '',
      kind: doc.kind || 'file',
      url: doc.url || null,
    });
    setErrors({});
    setMode('edit');
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editingDoc) return;
    const next = validateForm(editForm, false);
    setErrors(next);
    if (Object.keys(next).length) return;
    if (!editForm.file) {
      toast({
        title: 'Choose a replacement file to re-upload',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }
    if (!asset?.id) return;
    setBusy(true);
    try {
      if (editingDoc.id) await deleteDocument(editingDoc.id);
      await uploadDocument({
        ownerType: 'asset',
        ownerId: asset.id,
        docType: editForm.label.trim() || editForm.group,
        file: editForm.file,
      });
      toast({
        title: 'Document replaced',
        description: editForm.label.trim(),
        status: 'success',
        position: 'top-right',
      });
      setEditingDoc(null);
      setMode('list');
      await reloadDocs();
      onSaved?.();
    } catch (err) {
      toast({
        title: err?.message || 'Failed to update document',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (doc) => {
    if (!doc?.id) return;
    setBusy(true);
    try {
      await deleteDocument(doc.id);
      toast({
        title: 'Document removed',
        description: doc.label,
        status: 'info',
        position: 'top-right',
      });
      await reloadDocs();
      onSaved?.();
    } catch (err) {
      toast({
        title: err?.message || 'Failed to delete document',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setBusy(false);
    }
  };

  const quickAddSide = (label) => {
    setAddForm({ ...emptyForm, group: 'sides', label });
    setMode('add');
    setErrors({});
  };

  if (!asset) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        bg="bg.surface"
        borderRadius="md"
        mx={4}
        my={4}
        maxH="90vh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader flexShrink={0}>
          Manage documents · {asset.assetId}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody flex="1" overflowY="auto" pb={4}>
          <VStack align="stretch" spacing={4}>
            {mode === 'list' ? (
              <>
                <Flex gap={2} wrap="wrap" align="center" justify="space-between">
                  <AppSelect
                    label="Filter by group"
                    value={groupFilter}
                    onChange={(v) => setGroupFilter(v ?? '')}
                    options={[
                      { value: '', label: 'All documents' },
                      ...DOCUMENT_GROUPS,
                    ]}
                    maxW={{ base: '100%', md: '320px' }}
                  />
                  <OutlineButton
                    size="sm"
                    leftIcon={<MdAdd />}
                    onClick={() => {
                      setAddForm({ ...emptyForm, group: groupFilter || 'compliance' });
                      setErrors({});
                      setMode('add');
                    }}
                  >
                    Add document
                  </OutlineButton>
                </Flex>

                {groupFilter === 'sides' || groupFilter === '' ? (
                  <Box
                    p={3}
                    bg="bg.amberTint"
                    borderRadius="sm"
                    borderWidth="1px"
                    borderColor="utkarsh.amber"
                  >
                    <Text fontSize="13px" color="text.primary" mb={2}>
                      Quick add side photo slots:
                    </Text>
                    <Flex gap={2} wrap="wrap">
                      {SIDE_LABELS.map((label) => (
                        <OutlineButton
                          key={label}
                          size="xs"
                          onClick={() => quickAddSide(label)}
                        >
                          + {label}
                        </OutlineButton>
                      ))}
                    </Flex>
                  </Box>
                ) : null}

                <Box overflowX="auto" borderWidth="1px" borderColor="border.subtle" borderRadius="sm">
                  <Table variant="simple" size="sm">
                    <Thead bg="bg.input">
                      <Tr>
                        <SortableTh columnKey="label" sort={sort} onSort={toggleSort}>
                          Label
                        </SortableTh>
                        <SortableTh columnKey="group" sort={sort} onSort={toggleSort}>
                          Group
                        </SortableTh>
                        <SortableTh columnKey="fileName" sort={sort} onSort={toggleSort}>
                          File
                        </SortableTh>
                        <Th w="100px">Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {sortedDocs.map((doc) => (
                        <Tr key={doc.id}>
                          <Td fontWeight="600" fontSize="13px">
                            {doc.label}
                          </Td>
                          <Td>
                            <StatusBadge status="neutral">{doc.group}</StatusBadge>
                          </Td>
                          <Td fontFamily="mono" fontSize="12px" maxW="200px">
                            <Flex align="center" gap={2}>
                              <Icon as={docIcon(doc.kind)} boxSize={4} />
                              <Text noOfLines={1}>{doc.fileName}</Text>
                            </Flex>
                          </Td>
                          <Td>
                            <Flex gap={1}>
                              <IconButton
                                aria-label="Edit document"
                                icon={<MdEdit />}
                                size="sm"
                                variant="ghost"
                                onClick={() => openEdit(doc)}
                              />
                              <IconButton
                                aria-label="Remove document"
                                icon={<MdDelete />}
                                size="sm"
                                variant="ghost"
                                colorScheme="red"
                                onClick={() => handleRemove(doc)}
                              />
                            </Flex>
                          </Td>
                        </Tr>
                      ))}
                      {filteredDocs.length === 0 ? (
                        <Tr>
                          <Td colSpan={4}>
                            <Text py={6} textAlign="center" color="text.secondary">
                              No documents in this group. Use Add document.
                            </Text>
                          </Td>
                        </Tr>
                      ) : null}
                    </Tbody>
                  </Table>
                </Box>
              </>
            ) : null}

            {mode === 'add' ? (
              <Box as="form" onSubmit={handleAdd}>
                <DocumentForm
                  title="Add document"
                  form={addForm}
                  setForm={setAddForm}
                  errors={errors}
                  fileInputRef={addFileRef}
                />
                <Flex gap={2} mt={4}>
                  <OutlineButton type="button" onClick={() => setMode('list')}>
                    Back
                  </OutlineButton>
                  <PrimaryButton type="submit">Save document</PrimaryButton>
                </Flex>
              </Box>
            ) : null}

            {mode === 'edit' && editingDoc ? (
              <Box as="form" onSubmit={handleEdit}>
                <DocumentForm
                  title={`Edit · ${editingDoc.label}`}
                  form={editForm}
                  setForm={setEditForm}
                  errors={errors}
                  fileInputRef={editFileRef}
                />
                <Flex gap={2} mt={4}>
                  <OutlineButton type="button" onClick={() => setMode('list')}>
                    Back
                  </OutlineButton>
                  <PrimaryButton type="submit">Update document</PrimaryButton>
                </Flex>
              </Box>
            ) : null}
          </VStack>
        </ModalBody>
        {mode === 'list' ? (
          <ModalFooter gap={2} borderTopWidth="1px" borderColor="border.subtle">
            <OutlineButton type="button" onClick={onClose}>
              Done
            </OutlineButton>
          </ModalFooter>
        ) : null}
      </ModalContent>
    </Modal>
  );
}
